<template>
  <div
    role="alert"
    data-test="restricted-access"
    :data-entitlement-key="entitlementKey"
    class="flex flex-col items-center justify-center rounded-theme-md text-center"
    :class="[compact ? 'gap-2 px-4 py-4' : 'gap-4 px-6 py-10', containerClass]"
  >
    <!-- Lock icon (amber, not an error) -->
    <div
      class="flex items-center justify-center rounded-full bg-theme-palette-amber/10 dark:bg-theme-palette-amber/20"
      :class="compact ? 'h-9 w-9' : 'h-12 w-12'"
    >
      <svg
        class="text-theme-palette-amber dark:text-theme-palette-amber"
        :class="compact ? 'h-5 w-5' : 'h-6 w-6'"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 11c0-1.105.895-2 2-2s2 .895 2 2m-8 0V7a4 4 0 118 0v4m-9 0h10a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6a1 1 0 011-1z"
        />
      </svg>
    </div>

    <!-- Title -->
    <div>
      <h3
        class="font-semibold text-theme-text"
        :class="compact ? 'text-sm' : 'text-base'"
      >
        {{ title || $t('dataset.restricted.title') }}
      </h3>
      <p
        class="mt-1 text-theme-text-secondary"
        :class="compact ? 'text-xs' : 'text-sm'"
      >
        {{ resolvedMessage }}
      </p>
    </div>

    <!-- Upgrade CTA / custom actions via slot. The button navigates straight
         into the subscription upgrade flow (deep-linked to the required plan
         when the 403 carried an offer), so every locked visualization gets a
         working upgrade path without each parent wiring it up. -->
    <slot>
      <button
        v-if="showUpgrade"
        type="button"
        data-test="restricted-access-upgrade"
        class="inline-flex items-center gap-1.5 rounded-full border border-theme-palette-amber/40 bg-theme-surface px-4 py-1.5 text-sm font-medium text-theme-palette-amber transition hover:bg-theme-palette-amber/10 dark:border-theme-palette-amber dark:bg-transparent dark:text-theme-palette-amber dark:hover:bg-theme-palette-amber/20"
        @click="onUpgrade"
      >
        {{ $t('dataset.restricted.cta') }}
      </button>
    </slot>
  </div>
</template>

<script>
import { goToUpgrade } from '@/utils/upgradeNavigation';

/**
 * AppRestrictedState — "you don't have access to this" state.
 *
 * Shown when the API returns HTTP 403 (subscription does not include this
 * dataset/feature), in place of the generic AppErrorState. Mirrors
 * AppErrorState's API (title/message/compact/containerClass) so it is a
 * drop-in for the error branch of a visualization.
 *
 * The upgrade CTA is shown by default and navigates straight into the
 * subscription flow — deep-linked to the required plan when the caller passes
 * the 403's `offer`. Parents that want custom behaviour can still listen to
 * `@upgrade` or override the default slot.
 */
export default {
  name: 'AppRestrictedState',

  props: {
    /** Bold heading text (defaults to i18n dataset.restricted.title) */
    title: { type: String, default: '' },
    /** Descriptive sub-text (defaults to i18n dataset.restricted.message) */
    message: { type: String, default: '' },
    /** Tighter padding + smaller icon, for use inline inside a card body */
    compact: { type: Boolean, default: false },
    /** Show the default upgrade CTA button (ignored if default slot is used) */
    showUpgrade: { type: Boolean, default: true },
    /** Extra classes on the outer container */
    containerClass: { type: [String, Array, Object], default: '' },
    /** Canonical entitlement key this state is gating on (e.g.
     *  `dataset.epex-spot.read`). Surfaced as `data-entitlement-key` so QA can
     *  assert WHICH entitlement was missing without matching on copy. Also used
     *  to deep-link the upgrade flow to the gated capability. */
    entitlementKey: { type: String, default: '' },
    /** scrumble's resolved BillingOffer from the 403 detail (name /
     *  offer_type / stripe_price_id / self_serve). When present, the message
     *  reads "Requires <plan>" and the upgrade button deep-links to it. */
    offer: { type: Object, default: null },
  },

  emits: ['upgrade'],

  computed: {
    resolvedMessage() {
      if (this.message) {
        return this.message;
      }
      const planName = this.offer?.name;
      if (planName) {
        return this.$t('dataset.restricted.requires', { plan: planName });
      }
      return this.$t('dataset.restricted.message');
    },
  },

  methods: {
    onUpgrade() {
      // Let parents override/extend, then run the default navigation.
      this.$emit('upgrade');
      goToUpgrade(this.entitlementKey || null, this.offer);
    },
  },
};
</script>
